import { Currency } from "@redotech/money/currencies";
import { MoneyValue } from "@redotech/money/money-calculator";
import * as z from "zod/v4";
import { ReturnProductStrategy } from "../../return";

export interface ProductTotals<C extends Currency> {
  id: string;
  type: ReturnProductStrategy;
  /**
   * The return value for the product. This is the value that will be used in other return calculations,
   * all other product totals are used to calculate this value
   */
  returnValue: MoneyValue<C>;
  /** The new order value for the product. Will be zero unless the product has a variant exchange/exchange group */
  variantNewOrderValue: MoneyValue<C>;
  /** The price adjustment applied by the return flow */
  returnFlowPriceAdjustment: MoneyValue<C>;
  /** The merchant adjustment manually applied in the return details page */
  merchantManualAdjustment: MoneyValue<C>;
  /** The price after the discount has been applied */
  discountPrice: MoneyValue<C>;
  /**
   * The discount for the product (generally just the line item discount divided by quantity, but can be influenced by
   * discount distribution, bundles, and returns of exchanges)
   */
  discount: MoneyValue<C>;
  /** The price for the product (generally just the line item price, but can be influenced by bundles and returns of exchanges) */
  price: MoneyValue<C>;
  /** The tax for the product (generally just the line item tax divided by quantity, but can be bundles and returns of exchanges) */
  tax: MoneyValue<C>;
}

export interface ReturnTotals<C extends Currency> {
  /** A list of totals associated with each product, including the return value and all the values that were used to calculate it */
  productTotalsList: ProductTotals<C>[];
  /** The amount of store credit that will be issued to the customer */
  storeCredit: MoneyValue<C>;
  /** The amount of refund that will be issued to the customer */
  refund: MoneyValue<C>;
  /** The amount of outstanding balance that the customer owes */
  outstandingBalance: MoneyValue<C>;
  /** The total price of all exchange items (post discounts and taxes) */
  newOrderValue: MoneyValue<C>;
  /**
   * For returns with usePreDiscountPrice enabled, this is the sum of discounts across all return products
   * that will be applied as a use-it-or-lose-it shop now bonus
   */
  preDiscountShopNowBonus: MoneyValue<C>;
}

interface ExchangeItemTotals<C extends Currency> {
  price: MoneyValue<C>;
  discount: MoneyValue<C>;
  discountPrice: MoneyValue<C>;
}

export interface ProductSummaryTotals<C extends Currency>
  extends ProductTotals<C> {
  returnValuePreTax: MoneyValue<C>;
}

export interface ReturnSummaryTotals<C extends Currency>
  extends Omit<ReturnTotals<C>, "returnData"> {
  newOrderSubtotal: MoneyValue<C>;
  newOrderTaxes: MoneyValue<C>;
  totalsForExchangeItems: ExchangeItemTotals<C>[];
  returnValue: MoneyValue<C>;
  productTotalsList: ProductSummaryTotals<C>[];
}

/**
 * Helper type to recursively serialize MoneyValue fields to numbers
 * This allows us to maintain a single source of truth for our types
 * while still being able to serialize them for RPC responses
 */
type SerializeMoneyValues<T> =
  T extends MoneyValue<any>
    ? number // Convert MoneyValue to number
    : T extends Array<infer U>
      ? Array<SerializeMoneyValues<U>> // Handle arrays recursively
      : T extends object
        ? { [K in keyof T]: SerializeMoneyValues<T[K]> } // Recurse into objects
        : T; // Keep primitives as-is

/**
 * Serialized version of ReturnSummaryTotals that can be sent over RPC
 */
export type SerializedReturnSummaryTotals = SerializeMoneyValues<
  ReturnSummaryTotals<Currency>
>;

export type SerializedProductSummaryTotals = SerializeMoneyValues<
  ProductSummaryTotals<Currency>
>;

export const ProductSummaryTotalsSchema: z.ZodType<SerializedProductSummaryTotals> =
  z.object({
    id: z.string(),
    type: z.enum(ReturnProductStrategy),
    returnValue: z.number(),
    variantNewOrderValue: z.number(),
    returnFlowPriceAdjustment: z.number(),
    merchantManualAdjustment: z.number(),
    discountPrice: z.number(),
    discount: z.number(),
    price: z.number(),
    tax: z.number(),
    returnValuePreTax: z.number(),
  });

export const ReturnSummaryTotalsSchema: z.ZodType<SerializedReturnSummaryTotals> =
  z.object({
    productTotalsList: z.array(ProductSummaryTotalsSchema),
    storeCredit: z.number(),
    refund: z.number(),
    outstandingBalance: z.number(),
    newOrderValue: z.number(),
    newOrderSubtotal: z.number(),
    newOrderTaxes: z.number(),
    totalsForExchangeItems: z.array(
      z.object({
        price: z.number(),
        discount: z.number(),
        discountPrice: z.number(),
      }),
    ),
    returnValue: z.number(),
    preDiscountShopNowBonus: z.number(),
  });
